<template>
  <c-box>
    <c-flex mb="2xl" :flex-direction="{ base: 'column', lg: 'row' }">
      <c-heading
        as="h4"
        :font-size="{ base: 'xl', md: '3xl' }"
        font-weight="normal"
        :mb="{ base: 'md', lg: '0' }"
      >
        {{ $t("frontPage.ordersAndReturns.title") }}
      </c-heading>
      <c-flex ml="auto" :flex-direction="{ base: 'column', sm: 'row' }">
        <router-link to="/orders" v-chakra :mr="{ sm: 'md' }" :mb="{ base: 'md', sm: '0' }">
          <c-button right-icon="chevron_right" tabindex="-1">
            {{ $t("frontPage.ordersAndReturns.showAllOrders") }}
          </c-button>
        </router-link>
        <router-link to="/support/returns">
          <c-button right-icon="chevron_right" tabindex="-1">
            {{ $t("frontPage.ordersAndReturns.showAllReturns") }}
          </c-button>
        </router-link>
      </c-flex>
    </c-flex>

    <c-box class="orders-and-returns-status">
      <c-box
        v-for="(item, index) in ordersAndReturns"
        :key="item.id"
        @click="showDetails(item)"
        @keydown.enter="showDetails(item)"
        tabindex="0"
      >
        <c-grid
          :template-columns="{ md: 'auto 2fr 2fr 3fr auto 3fr' }"
          :py="{ base: 'md', md: '3xl', xl: '3rem' }"
          border-top="1px solid"
          :border-top-color="index === 0 ? 'brand.grayDark' : 'transparent'"
          border-bottom="1px solid"
          border-bottom-color="brand.grayDark"
          cursor="pointer"
          :template-areas="`
            'icon id'
            'dateLabel date'
            'refLabel ref'
            'statusLabel progress'
            '. status'
          `"
          :data-cy="isMaintenanceReturn(item) ? 'return_item' : 'order_item'"
        >
          <c-box v-if="isMaintenanceReturn(item)" display="contents">
            <c-box mr="3xl" :grid-area="{ base: 'icon', md: 'initial' }">
              <c-icon name="out" color="brand.purple" size="1.4rem" />
            </c-box>

            <c-box
              :mr="{ md: 'xl' }"
              :grid-area="{ base: 'id', md: 'initial' }"
              :font-weight="{ base: 'bold', md: 'normal' }"
              :text-align="{ base: 'right', md: 'left' }"
            >
              {{ $t("frontPage.ordersAndReturns.return") }} #{{ getReturnId(item.rmaId) }}
            </c-box>

            <c-box mr="3xl" :d="{ base: 'contents', md: 'block' }">
              <c-text as="span" :grid-area="{ base: 'dateLabel', md: 'initial' }">
                {{ $t("common.created") }}
              </c-text>
              <c-text
                as="span"
                :grid-area="{ base: 'date', md: 'initial' }"
                :text-align="{ base: 'right', md: 'left' }"
              >
                {{ item.created | moment("D.M.YYYY") }}
              </c-text>
            </c-box>

            <c-text grid-area="refLabel" :d="{ md: 'none' }">
              {{ $t("productReturnV2.sites") }}
            </c-text>

            <c-box
              :mr="{ md: '3xl' }"
              :grid-area="{ base: 'ref', md: 'initial' }"
              :max-width="{ base: '10rem', md: 'none' }"
              :text-align="{ base: 'right', md: 'left' }"
              :ml="{ base: 'auto', md: '0' }"
            >
              <c-text :title="getSiteNames(item).full">
                {{ getSiteNames(item).short }}
              </c-text>
            </c-box>

            <c-text grid-area="statusLabel" :d="{ md: 'none' }">
              {{ $t("common.status") }}
            </c-text>

            <c-box
              :mr="{ md: '3xl' }"
              :width="{ md: '8.4rem' }"
              :grid-area="{ base: 'progress', md: 'initial' }"
            >
              <c-box
                v-chakra="{
                  '.return-progress > div': {
                    backgroundColor: 'brand.purple',
                  },
                  '.FINISHED > div': {
                    backgroundColor: 'brand.green',
                  },
                  '.CANCELLED > div': {
                    backgroundColor: 'brand.redLight',
                  },
                  '.UNKNOWN > div': {
                    backgroundColor: 'brand.purpleLight',
                  },
                }"
              >
                <c-progress
                  v-if="isMaintenanceReturn(item)"
                  class="return-progress"
                  :class="item.status"
                  :value="item.progress"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                />
                <c-progress
                  v-else
                  class="UNKNOWN"
                  has-stripe
                  :value="100"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                />
              </c-box>
            </c-box>
            <c-box
              :grid-area="{ base: 'status', md: 'initial' }"
              :text-align="{ base: 'right', md: 'left' }"
            >
              <c-box :title="$t('productReturnV2.columnTitles.status')">
                <c-box v-if="item.status">
                  {{ $t(`productReturnV2.returnStatus.${item.status}`) }}
                </c-box>
                <c-box v-else>
                  {{ $t(`productReturnV2.returnStatus.UNKNOWN`) }}
                </c-box>
                <c-box v-if="item.status">
                  <c-box v-if="hasQuotationBeenSent(item)">
                    <c-icon name="alert" size="1.4rem" color="brand.orange" />
                    {{ $t("productReturnV2.warning.REPAIR_QUOTATION_SENT_TO_CUSTOMER") }}
                  </c-box>
                </c-box>
              </c-box>
            </c-box>
          </c-box>

          <!-- orders -->
          <c-box v-else display="contents">
            <c-box mr="3xl" :grid-area="{ base: 'icon', md: 'initial' }">
              <c-icon name="in" color="brand.greenDark" size="1.4rem" />
            </c-box>

            <c-box
              :mr="{ md: 'xl' }"
              :grid-area="{ base: 'id', md: 'initial' }"
              :font-weight="{ base: 'bold', md: 'normal' }"
              :text-align="{ base: 'right', md: 'left' }"
            >
              {{ $t("frontPage.ordersAndReturns.order") }} #{{ item.orderNumber }}
            </c-box>

            <c-box mr="3xl" :d="{ base: 'contents', md: 'block' }">
              <c-text as="span" :grid-area="{ base: 'dateLabel', md: 'initial' }">
                {{ $t("common.created") }}
              </c-text>
              <c-text
                as="span"
                :grid-area="{ base: 'date', md: 'initial' }"
                :text-align="{ base: 'right', md: 'left' }"
              >
                {{ item.timestamp | moment("D.M.YYYY") }}
              </c-text>
            </c-box>

            <c-text grid-area="refLabel" :d="{ md: 'none' }">
              {{ $t("productReturnV2.sites") }}
            </c-text>

            <c-box
              :mr="{ md: '3xl' }"
              :grid-area="{ base: 'ref', md: 'initial' }"
              :max-width="{ base: '10rem', md: 'none' }"
              :text-align="{ base: 'right', md: 'left' }"
              :ml="{ base: 'auto', md: '0' }"
            >
              <c-text>
                {{ item.deliveryDetails.reference }}
              </c-text>
            </c-box>

            <c-text grid-area="statusLabel" :d="{ md: 'none' }">
              {{ $t("common.status") }}
            </c-text>

            <c-box
              :mr="{ md: '3xl' }"
              :width="{ md: '8.4rem' }"
              :grid-area="{ base: 'progress', md: 'initial' }"
            >
              <c-box
                v-chakra="{
                  '.order-progress > div': {
                    backgroundColor: 'brand.purple',
                  },
                  '.SHIPPED > div, .HANDLED > div, .FINISHED > div': {
                    backgroundColor: 'brand.green',
                  },
                  '.FAIL > div, .CANCELLED > div': {
                    backgroundColor: 'brand.redLight',
                  },
                }"
              >
                <c-progress
                  v-if="item.orderStatus"
                  class="order-progress"
                  :class="item.orderStatus"
                  :value="orderProgress[item.orderStatus]"
                  border-radius="full"
                  size="lg"
                  background="gray.200"
                />
              </c-box>
            </c-box>

            <c-box
              :grid-area="{ base: 'status', md: 'initial' }"
              :text-align="{ base: 'right', md: 'left' }"
            >
              <c-text v-if="item.orderStatus">
                {{ $t(`ordering.orderStatuses.${item.orderStatus}`) }}
              </c-text>
            </c-box>
          </c-box>
        </c-grid>
      </c-box>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { mapState } from "vuex";
import { defineComponent } from "vue";
import moment from "moment-timezone";
import { filterOrdersByStatusAndAge, filterReturnsByAge } from "@/util/DataFilter";
import {
  getCroppedReturnId,
  IMaintenanceReturnResult,
  isMaintenanceReturn,
  quotationSentForSomeLine,
} from "@iloq/shared";
import Icon from "@/design-system/elements/Icon.vue";
import { progressPercentageByOrderStatus } from "../constants";
import { CBox, CButton, CFlex, CGrid, CHeading, CIcon, CProgress, CText } from "@chakra-ui/vue";

export default defineComponent({
  name: "CurrentOrdersAndReturns",
  components: {
    Icon,
    CBox,
    CFlex,
    CButton,
    CIcon,
    CHeading,
    CGrid,
    CProgress,
    CText,
  },
  computed: {
    ...mapState("orders", {
      orders: "orders",
    }),
    ...mapState("returns", {
      returns: "list",
    }),
    ordersAndReturns() {
      const freshOrdersWithValidStatus = filterOrdersByStatusAndAge(this.orders, "DRAFT", 12);
      const freshMaintenanceReturns = filterReturnsByAge(this.returns, 12);
      const ordersAndReturns = [...freshOrdersWithValidStatus, ...freshMaintenanceReturns];
      ordersAndReturns.sort((a, b) => {
        function getDate(item) {
          if (item.rmaId !== undefined) {
            return moment(item.created).toDate();
          }
          return moment(item.timestamp).toDate();
        }
        const isAfter = getDate(a).getTime() > getDate(b).getTime();
        return isAfter ? -1 : 1;
      });
      return ordersAndReturns.slice(0, 5);
    },
  },
  data() {
    return {
      orderProgress: progressPercentageByOrderStatus,
    };
  },
  methods: {
    getReturnId(rmaId: string): string {
      return getCroppedReturnId(rmaId);
    },
    isMaintenanceReturn(data: any): boolean {
      return isMaintenanceReturn(data.returnType);
    },
    hasQuotationBeenSent(data: IMaintenanceReturnResult): boolean {
      return quotationSentForSomeLine(data);
    },
    getSiteNames(data: IMaintenanceReturnResult): { short: string; full: string } {
      const MAX_LENGTH = 50;
      const sites = data.productDetails?.sites || [];
      const siteNames = sites
        .map((site) => site.siteDetails.name)
        .filter((name) => !!name)
        .join(", ");

      if (siteNames.length < MAX_LENGTH) {
        return { short: siteNames, full: siteNames };
      }

      return { short: `${siteNames.slice(0, MAX_LENGTH - 3)}...`, full: siteNames };
    },
    showDetails(data: any): void {
      if (this.isMaintenanceReturn(data)) {
        this.$router.push({ path: `/support/returns/maintenance_return/${data.rmaId}` });
      } else {
        this.$router.push({
          name: "view_order",
          params: { id: data.orderNumber, isConfirmationForNewOrder: "false" },
        });
      }
    },
  },
});
</script>
